import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Eye, FileText } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { getDownloadUrl } from './participantDocumentsApi';

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

export function DocumentVersionHistoryModal({ doc, participantId, open, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !doc) return;
    setLoading(true);
    api.get(`/participants/${participantId}/documents/${doc.id}/versions`)
      .then(r => setVersions(r.data.data || []))
      .catch(() => toast.error('Failed to load version history'))
      .finally(() => setLoading(false));
  }, [open, doc, participantId]);

  const handleDownload = async (v) => {
    try {
      const url = getDownloadUrl(participantId, v.id);
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = v.fileName || v.title || 'document';
      link.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error('Failed to download');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="version-history-modal">
        <DialogHeader>
          <DialogTitle className="text-sm">Version History</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No version history</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-auto" data-testid="version-list">
            {versions.map((v) => (
              <div key={v.id} className={`flex items-center gap-3 p-3 rounded-lg border ${v.isLatest ? 'bg-primary/5 border-primary/20' : 'bg-muted/20'}`} data-testid={`version-row-${v.id}`}>
                <div className="flex-shrink-0 p-1.5 rounded bg-muted/50">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">v{v.version || 1}</span>
                    {v.isLatest && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">Latest</Badge>}
                    {v.status === 'ARCHIVED' && <Badge variant="secondary" className="text-[9px]">Archived</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{v.title}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {v.uploadedByName || '—'} &middot; {formatDate(v.createdAt)}
                  </p>
                </div>
                <div className="flex gap-1">
                  {v.can_download && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(v)} title="Download">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
